import type { Conversation, InboxResponse } from 'types/interfaces/message';
import type { ThreadGroup } from 'types/interfaces/thread';

/**
 * Pure helpers behind the thread list (Asana 1219952262403623, Decision 5).
 * Rows arrive server-ordered by group (NEEDS_YOU, ACTIVE, EARLIER) then by
 * lastMessageAt, so the list only has to emit a header where the group
 * changes. Counts come from the response's groupCounts, which cover the whole
 * filtered set, not the page.
 */

export const GROUP_ORDER: readonly ThreadGroup[] = ['NEEDS_YOU', 'ACTIVE', 'EARLIER'];

export const GROUP_LABELS: Record<ThreadGroup, string> = {
  NEEDS_YOU: 'Needs you',
  ACTIVE: 'In progress',
  EARLIER: 'Earlier',
};

/** A row the backend did not group (legacy cache, older backend) is Earlier. */
export const groupOf = (row: Pick<Conversation, 'group'>): ThreadGroup => row.group ?? 'EARLIER';

export interface ThreadSection {
  group: ThreadGroup;
  label: string;
  /** From groupCounts when the backend sent them, else the rows on this page. */
  count: number;
  rows: Conversation[];
}

const countFor = (group: ThreadGroup, groupCounts: InboxResponse['groupCounts'] | undefined, onPage: number): number => {
  if (!groupCounts) return onPage;
  switch (group) {
    case 'NEEDS_YOU':
      return groupCounts.needsYou;
    case 'ACTIVE':
      return groupCounts.active;
    default:
      return groupCounts.earlier;
  }
};

/**
 * Split server-ordered rows into contiguous sections. The order the server
 * sent is kept; a new section starts whenever the group changes, so a backend
 * that interleaves groups still renders every row, each under its own header.
 */
export const sectionRows = (
  rows: Conversation[],
  groupCounts: InboxResponse['groupCounts'] | undefined,
): ThreadSection[] => {
  const sections: ThreadSection[] = [];
  for (const row of rows) {
    const group = groupOf(row);
    const last = sections[sections.length - 1];
    if (last && last.group === group) {
      last.rows.push(row);
    } else {
      sections.push({ group, label: GROUP_LABELS[group], count: 0, rows: [row] });
    }
  }
  for (const section of sections) {
    section.count = countFor(section.group, groupCounts, section.rows.length);
  }
  return sections;
};

/** The row's headline: the subject title when typed, else the person. */
export const rowTitle = (row: Conversation): string =>
  row.subjectTitle && row.subjectTitle.trim() ? row.subjectTitle : row.otherUser.username;

/** Strip HTML out of a stored last message so the preview is plain text. */
export const stripHTML = (html: string): string => {
  if (typeof DOMParser === 'undefined') return html.replace(/<[^>]+>/g, '');
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

/** "username · last message preview", the row's second line. */
export const rowSecondLine = (row: Conversation): string => {
  const preview = stripHTML(row.lastMessage || '').replace(/\s+/g, ' ').trim();
  return preview ? `${row.otherUser.username} · ${preview}` : row.otherUser.username;
};

/** Time inside the last day, else a short date. */
export const formatShortTime = (timestamp: string, now: Date = new Date()): string => {
  const messageDate = new Date(timestamp);
  if (Number.isNaN(messageDate.getTime())) return '';
  const diffHours = (now.getTime() - messageDate.getTime()) / (1000 * 60 * 60);
  if (diffHours < 24) {
    return messageDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  }
  if (messageDate.getFullYear() === now.getFullYear()) {
    return messageDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  return messageDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
};
